import type { ComponentChildren, JSX } from 'preact';
import { ZONES, DOW_TH } from '@shared/types';
import { cx } from '../lib/format';

export function Field({ label, hint, error, children, id }: { label?: ComponentChildren; hint?: ComponentChildren; error?: string | null; children: ComponentChildren; id?: string }) {
  return (
    <div class="field">
      {label && <label for={id}>{label}</label>}
      {children}
      {error ? <div class="err">{error}</div> : hint ? <div class="hint">{hint}</div> : null}
    </div>
  );
}

type InputProps = JSX.InputHTMLAttributes<HTMLInputElement> & { onValue?: (v: string) => void };
export function Input({ onValue, class: cls, ...rest }: InputProps) {
  return <input class={cx('input', cls as string)} {...rest} onInput={(e) => onValue?.((e.currentTarget as HTMLInputElement).value)} />;
}

type TextAreaProps = JSX.TextareaHTMLAttributes<HTMLTextAreaElement> & { onValue?: (v: string) => void };
export function TextArea({ onValue, class: cls, ...rest }: TextAreaProps) {
  return <textarea class={cx('textarea', cls as string)} {...rest} onInput={(e) => onValue?.((e.currentTarget as HTMLTextAreaElement).value)} />;
}

type SelectProps = JSX.SelectHTMLAttributes<HTMLSelectElement> & { onValue?: (v: string) => void; children: ComponentChildren };
export function Select({ onValue, class: cls, children, ...rest }: SelectProps) {
  return (
    <select class={cx('select', cls as string)} {...rest} onChange={(e) => onValue?.((e.currentTarget as HTMLSelectElement).value)}>
      {children}
    </select>
  );
}

export function ZoneSelect({ value, onValue, allLabel = 'ทุกโซน', id }: { value: string; onValue: (v: string) => void; allLabel?: string | null; id?: string }) {
  return (
    <Select id={id} value={value} onValue={onValue} aria-label="โซน">
      {allLabel != null && <option value="">{allLabel}</option>}
      {ZONES.map((z) => (
        <option key={z} value={z}>
          {z}
        </option>
      ))}
    </Select>
  );
}

export function DowPicker({ value, onChange }: { value: number[]; onChange: (v: number[]) => void }) {
  const toggle = (d: number) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort());
  return (
    <div class="dow-picker" role="group" aria-label="วันที่มาประจำ">
      {DOW_TH.map((n, d) => (
        <button type="button" key={d} class={cx(value.includes(d) && 'on')} aria-pressed={value.includes(d) ? 'true' : 'false'} onClick={() => toggle(d)}>
          {n}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: ComponentChildren }) {
  return (
    <label class="switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)} />
      <span>{label}</span>
    </label>
  );
}

export function OptionCards<T extends string | number>({ options, value, onChange }: { options: { value: T; title: string; desc?: string; n?: string }[]; value: T | null; onChange: (v: T) => void }) {
  return (
    <div class="optcards" role="radiogroup">
      {options.map((o) => (
        <button type="button" key={String(o.value)} role="radio" aria-checked={value === o.value ? 'true' : 'false'} class={cx('optcard', value === o.value && 'on')} onClick={() => onChange(o.value)}>
          {o.n != null && <span class="n">{o.n}</span>}
          <span class="grow">
            <div class="t">{o.title}</div>
            {o.desc && <div class="d">{o.desc}</div>}
          </span>
        </button>
      ))}
    </div>
  );
}

export function StarRating({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  return (
    <div class="rating" role={onChange ? 'radiogroup' : undefined} aria-label={`${value} ดาว`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} class={cx(n <= value && 'on')} disabled={!onChange} onClick={() => onChange?.(n)} aria-label={`${n} ดาว`}>
          ★
        </button>
      ))}
    </div>
  );
}
